<template>
  <ul
    data-test="root"
    class="bg-elev-context-menu-bg border border-bor-nav rounded-12 py-1 text-1316 text-fg-base-fg3 mr-2 whitespace-nowrap w-80"
  >
    <template
      v-for="item in items"
      :key="item.id"
    >
      <li
        v-if="item.borderTop"
        class="h-px my-2 bg-bor-nav"
      />
      <call-session-member-menu-item
        :item="item"
        @click.stop="onClick(item)"
      />
      <li
        v-if="item.borderBottom"
        class="h-px my-2 bg-bor-nav"
      />
    </template>
  </ul>
</template>

<script setup lang="ts">
import { IconName, useRenderNotifier } from '@wui/common-library'
import { ConferenceType } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import { useStoreConferenceMediaStatuses, useModeratedConferenceParticipantsSignalsStore } from '@/entities/conference-participant'
import { useStoreRooms, useIntercomRoomParticipantsStatusesStore, MemberStatus } from '@/entities/room'
import { useRoom } from '@/entities/use-room'
import { useStoreProfile } from '@/entities/user-profile'
import { useWebRTC } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useLocalization } from '@/shared/composables/localization'
import { useAutoAnswer } from '@/shared/composables/use-auto-answer'
import { useRoomControl } from '@/shared/composables/use-room-control'
import { useUserFeatures } from '@/shared/composables/user-features'
import { Contact } from '@/shared/types/api'
import { FeaturesNames } from '@/shared/types/settings'
import { Room } from '@/shared/types/store'
import { PSTNGUEST } from '@/shared/utils/constants'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUser } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

import { CallSessionMemberMenuItem as CallSessionMemberMenuItemType } from '..'

import CallSessionMemberMenuItem from './call-session-member-menu-item.vue'

const props = defineProps<{
  room: Room,
  member: Contact,
  isPinned: boolean,
}>()

const emit = defineEmits<{
  (event: 'togglePin'): void
  (event: 'seeProfileClick'): void
  (event: 'close'): void
}>()

const { t } = useLocalization()
const { currentUser } = useCurrentUser()
const { changeSubscriberRole } = useRoom()
const { notifySuccess, notifyWarning } = useRenderNotifier()
const {
  getParticipantMediaStatus,
  getConferenceCallsByImLogin,
  getStatusesByRoom,
  getConferenceParticipants,
} = useStoreConferenceMediaStatuses()
const {
  memberUnmuteAudio,
  memberMuteAudio,
  memberMuteVideo,
  memberUnmuteVideo,
  memberKick,
  memberHold,
  memberUnHold,
  memberCall,
  handOff,
} = useRoomControl()
const { getParticipantStatus, getIsParticipantIsActive } = useIntercomRoomParticipantsStatusesStore()
const { registered } = useWebRTC()
const storeRooms = useStoreRooms()
const { registerAutoAnswer } = useAutoAnswer()
const { currentGuest } = useGuestAuth()
const { hasFeature } = useUserFeatures()
const {
  sendAudioEnablingBanned,
  sendVideoEnablingBanned,
  getIsAllowedVideoEnabling,
  getIsAllowedAudioEnabling,
  sendAudioEnablingAllowed,
  sendVideoEnablingAllowed,
  sendMediaEnablingRequest,
} = useModeratedConferenceParticipantsSignalsStore()
const { isVideoCodecsEnabled } = storeToRefs(useStoreProfile())

const isRoleChanging = ref(false)
const isMicToggling = ref(false)
const isEveryoneMuting = ref(false)
const isVideoToggling = ref(false)
const isHoldToggling = ref(false)
const callInProgress = ref(false)
const handDownInProgress = ref(false)
const memberKickInProgress = ref(false)
const isMicBanInProgress = ref(false)
const isVideoBanInProgress = ref(false)
const isMicEnableInProgress = ref(false)
const isVideoEnableInProgress = ref(false)
const isMicEnablingRequestInProgress = ref(false)
const isVideoEnablingRequestInProgress = ref(false)
const isMediaEnablingRequestInProgress = ref(false)

const isIAmModerator = computed(() => {
  const moderators = props.room.contacts?.filter(c => c.role === 'Moderator')
  return !!moderators?.find(i => i.id === currentUser.value?.imLogin)
})

const isExternalContact = computed(() => {
  return props.member.type === 'pstnGuest' || props.member.type === 'guest' || !props.member.imLogin
})

const memberRole = computed(() => {
  const contact = props.room.contacts?.find((c) => {
    return c.id === props.member.imLogin
  })

  return contact?.role
})

const participants = computed(() => getConferenceCallsByImLogin(props.room, props.member.imLogin))
const participantsStatuses = computed(() => participants.value.map(p => getParticipantMediaStatus(p.callId)))

const memberInternalNumber = computed(() => {
  if (props.member.type === 'pstnGuest') {
    return `${PSTNGUEST}-${props.member.internalNumber}`
  }
  if (props.member.type === 'guest') {
    return props.member.internalNumber || fromPServedUser(props.member.imLogin)
  }
  return props.member.internalNumber
})

const isIAmGuest = computed(() => !!currentGuest.value && !currentUser.value)
const pServed = computed(() => props.room.pServedUser)
const conferenceParticipants = computed(() => getConferenceParticipants(props.room))
const isOthersMuted = computed(() => {
  const others = conferenceParticipants.value.filter(p => !participants.value.map(p => p.callId).includes(p.callId))
  return others
    .map(p => getParticipantMediaStatus(p.callId))
    .filter(s => s?.isAudioMuted)
    .length === others.length
})
const items = computed<CallSessionMemberMenuItemType[]>(() => {
  const items: CallSessionMemberMenuItemType[] = [{
    id: 'seeProfile',
    label: t('chat.toSeeProfile'),
    icon: 'person',
  }]

  if (props.room.mode === ConferenceType.MODERATED && isIAmModerator.value && !isExternalContact.value && !isIAmGuest.value) {
    let icon: IconName = 'shieldRemove'
    if (memberRole.value === 'User') {
      icon = 'shieldAdd'
    }

    if (isRoleChanging.value) {
      icon = 'spinner'
    }

    items.push({
      id: 'changeRole',
      label: memberRole.value === 'User' ? t('chat.makeModerator') : t('chat.makeParticipant'),
      icon,
    })
  }

  if (props.room.mode === ConferenceType.MODERATED && isIAmModerator.value && memberRole.value === 'User' && getIsParticipantIsActive(pServed.value, props.member.internalNumber)) {
    const isSingleParticipant = participants.value.length === 1
    const isAudioNotMuted = !!participantsStatuses.value.find(s => !s?.isAudioMuted)
    const isVideoNotMuted = !!participantsStatuses.value.find(s => !s?.isVideoMuted)
    participants.value.forEach(p => {
      const isAudioEnablingAllowed = getIsAllowedAudioEnabling(props.room.pServedUser, p.callId)
      const isVideoEnablingAllowed = getIsAllowedVideoEnabling(props.room.pServedUser, p.callId)
      if (isAudioEnablingAllowed) {
        items.push({
          id: 'banAudio',
          label: `${t('calls.disableMicEnabling')} ${isSingleParticipant ? '' : ' ' + p.userAgent}`,
          icon: isMicBanInProgress.value ? 'spinner' :'micOffM',
          callId: p.callId,
        })
      } else {
        items.push({
          id: 'enableAudio',
          label: `${t('calls.enableMicEnabling')} ${isSingleParticipant ? '' : ' ' + p.userAgent}`,
          icon: isMicEnableInProgress.value ? 'spinner' :'micM',
          callId: p.callId,
        })
      }

      if (isVideoCodecsEnabled.value) {
        if (isVideoEnablingAllowed) {
          items.push({
            id: 'banVideo',
            label: `${t('calls.disableVideoEnabling')} ${isSingleParticipant ? '' : ' ' + p.userAgent}`,
            icon: isVideoBanInProgress.value ? 'spinner' :'videoCameraOffM',
            callId: p.callId,
          })
        } else {
          items.push({
            id: 'enableVideo',
            label: `${t('calls.enableVideoEnabling')} ${isSingleParticipant ? '' : ' ' + p.userAgent}`,
            icon: isVideoBanInProgress.value ? 'spinner' :'videoCameraM',
            callId: p.callId,
          })
        }
      }

      if (!isAudioNotMuted) {
        items.push({
          id: 'requestAudioEnabling',
          label: `${t('calls.requestTurnMic')} ${isSingleParticipant ? '' : ' ' + p.userAgent}`,
          icon: isMicEnablingRequestInProgress.value ? 'spinner' :'micM',
          borderTop: true,
          borderBottom: !isVideoCodecsEnabled.value || isVideoNotMuted,
          callId: p.callId,
        })
      }

      if (isVideoCodecsEnabled.value && !isVideoNotMuted) {
        items.push({
          id: 'requestVideoEnabling',
          label: `${t('calls.requestTurnVideo')} ${isSingleParticipant ? '' : ' ' + p.userAgent}`,
          icon: isVideoEnablingRequestInProgress.value ? 'spinner' :'videoCameraM',
          borderTop: isAudioNotMuted,
          borderBottom: isAudioNotMuted,
          callId: p.callId,
        })
      }

      if (!isVideoNotMuted && !isAudioNotMuted) {
        items.push({
          id: 'requestMediaEnabling',
          label: `${t('calls.requestTurnMicVideo')} ${isSingleParticipant ? '' : ' ' + p.userAgent}`,
          icon: isMediaEnablingRequestInProgress.value ? 'spinner' : '' as IconName,
          borderBottom: true,
          callId: p.callId,
        })
      }
    })
  }

  const canModerate = (props.room.mode === ConferenceType.MODERATED && isIAmModerator.value) || props.room.mode === ConferenceType.FREE_FOR_ALL
  if (canModerate && !isIAmGuest.value) {
    if (participants.value.length > 1) {
      participants.value.forEach(p => {
        if (getParticipantStatus(pServed.value, p.internalNumber, p.callId) === MemberStatus.Active) {
          const statuses = getParticipantMediaStatus(p.callId)
          const title = `${(statuses?.isAudioMuted ? t('common.turnOnMic') : t('common.turnOffMic'))} ${p.userAgent}`
          items.push({
            id: 'toggleMic',
            label: title,
            icon: isMicToggling.value ? 'spinner' : statuses?.isAudioMuted ? 'micOutlined' : 'micOffOutlined',
            callId: p.callId,
            title,
          })

          if (!getParticipantMediaStatus(p.callId)?.isAudioMuted && !isOthersMuted.value) {
            const title = `${t('common.muteEveryoneExceptThisParticipant')} ${p.userAgent}`
            items.push({
              id: 'muteEveryoneExceptThisParticipant',
              label: title,
              icon: isEveryoneMuting.value ? 'spinner' : 'micM',
              callId: p.callId,
              title,
            })
          }
        }
      })
    } else if (participants.value[0]) {
      const participant = participants.value[0]
      if (getParticipantStatus(pServed.value, participant.internalNumber, participant.callId) === MemberStatus.Active && !isOthersMuted.value) {
        const statuses = getParticipantMediaStatus(participant.callId)
        items.push({
          id: 'toggleMic',
          label: statuses?.isAudioMuted ? t('common.turnOnMic') : t('common.turnOffMic'),
          icon: isMicToggling.value ? 'spinner' : statuses?.isAudioMuted ? 'micOutlined' : 'micOffOutlined',
          callId: participant.callId,
        })

        if (!getParticipantMediaStatus(participant.callId)?.isAudioMuted) {
          items.push({
            id: 'muteEveryoneExceptThisParticipant',
            label: t('common.muteEveryoneExceptThisParticipant'),
            icon: isEveryoneMuting.value ? 'spinner' : 'micM',
            callId: participant.callId,
          })
        }
      }
    }

    // if (participants.value.length > 1) {
    //   participants.value.forEach(p => {
    //     if (getParticipantStatus(pServed.value, p.internalNumber, p.callId) === MemberStatus.Active) {
    //       const statuses = getParticipantMediaStatus(p.callId)
    //       const title = `${(statuses?.isVideoMuted ? t('calls.turnOnVideo') : t('calls.turnOffCam'))} ${p.userAgent}`
    //       items.push({
    //         id: 'toggleVideo',
    //         label: title,
    //         icon: isVideoToggling.value ? 'spinner' : statuses?.isVideoMuted ? 'videocam' : 'videocamOff',
    //         callId: p.callId,
    //         title,
    //       })
    //     }
    //   })
    // } else if (participants.value[0]) {
    //   const participant = participants.value[0]
    //   if (getParticipantStatus(pServed.value, participant.internalNumber, participant.callId) === MemberStatus.Active) {
    //     const statuses = getParticipantMediaStatus(participant.callId)
    //     items.push({
    //       id: 'toggleVideo',
    //       label: statuses?.isVideoMuted ? t('calls.turnOnVideo') : t('calls.turnOffCam'),
    //       icon: isVideoToggling.value ? 'spinner' : statuses?.isVideoMuted ? 'videocam' : 'videocamOff',
    //       callId: participant.callId,
    //     })
    //   }
    // }

    if (participants.value.length > 1) {
      participants.value.forEach(p => {
        const status = getParticipantStatus(pServed.value, p.internalNumber, p.callId)
        if ((status === MemberStatus.Active || status === MemberStatus.OnHold) && hasFeature(FeaturesNames.HOLD)) {
          const title = p.userAgent
          items.push({
            id: 'toggleHold',
            label: `${(status === MemberStatus.OnHold ? t('calls.endHold') : t('calls.startHold'))} ${title}`,
            icon: isHoldToggling.value ? 'spinner' :'phonePauseM',
            callId: p.callId,
            title,
          })
        }
      })
    } else if (participants.value[0]) {
      const participant = participants.value[0]
      const status = getParticipantStatus(pServed.value, participant.internalNumber, participant.callId)
      if ((status === MemberStatus.Active || status === MemberStatus.OnHold) && hasFeature(FeaturesNames.HOLD)) {
        items.push({
          id: 'toggleHold',
          label: status === 'OnHold' ? t('calls.endHold') : t('calls.startHold'),
          icon: isHoldToggling.value ? 'spinner' :'phonePauseM',
          callId: participant.callId,
        })
      }
    }

    if (!getIsParticipantIsActive(pServed.value, props.member.internalNumber)) {
      items.push({
        id: 'call',
        label: t('common.connect'),
        icon: registered ? 'phone' : 'callDisabled',
      })
    }
    if (!!Array.from(getStatusesByRoom(pServed.value)?.values() ?? []).find(c => c.id === props.member.imLogin && c.handEnabled)) {
      items.push({
        id: 'handDown',
        label: t('calls.lowerHand'),
        icon: handDownInProgress.value ? 'spinner' : 'handM',
      })
    }
  }

  if (!isExternalContact.value) {
    items.push({
      id: 'privateMessage',
      label: t('calls.writePrivateMessage'),
      icon: 'messageBalloonOutlined',
    })
  }

  if (!isIAmGuest.value) {
    items.push({
      id: 'pin',
      label: props.isPinned ? t('common.unpin') : t('common.pin'),
      icon: props.isPinned ? 'pinOffM' : 'pinM',
    })

    if (getIsParticipantIsActive(pServed.value, props.member.internalNumber) && canModerate) {
      items.push({
        id: 'kickMember',
        label: t('calls.disconnectFromConference'),
        icon: handDownInProgress.value ? 'spinner' : 'phoneDownM',
      })
    }
  }

  return items
})

const changeRole = async () => {
  isRoleChanging.value = true
  try {
    await changeSubscriberRole(props.room.pServedUser, memberInternalNumber.value, memberRole.value === 'Moderator' ? 'User' : 'Moderator')

    notifySuccess(t('chat.moderatorsHasBeenChanged'))
  } catch (e) {
    console.error(e)
  } finally {
    isRoleChanging.value = false
  }
}

const toggleMic = async (callId?: string) => {
  isMicToggling.value = true
  if (getParticipantMediaStatus(callId ?? '')?.isAudioMuted) {
    await memberUnmuteAudio(pServed.value, memberInternalNumber.value, callId)
  } else {
    await memberMuteAudio(pServed.value, memberInternalNumber.value, callId)
  }
  isMicToggling.value = false
}

const toggleVideo = async (callId?: string) => {
  isVideoToggling.value = true
  if (getParticipantMediaStatus(callId ?? '')?.isVideoMuted) {
    await memberUnmuteVideo(pServed.value, memberInternalNumber.value, callId)
  } else {
    await memberMuteVideo(pServed.value, memberInternalNumber.value, callId)
  }
  isVideoToggling.value = false
}

const toggleHold = async (callId?: string) => {
  isHoldToggling.value = true
  const status = getParticipantStatus(pServed.value, props.member.internalNumber, callId ?? '')
  if (status === MemberStatus.OnHold) {
    await memberUnHold(props.room.pServedUser, memberInternalNumber.value, callId)
  } else if (status === MemberStatus.Active) {
    await memberHold(props.room.pServedUser, memberInternalNumber.value, callId)
  }
  isHoldToggling.value = false
}

const call = (tel?: string) => {
  if (callInProgress.value) {
    return
  }
  callInProgress.value = true
  const room = storeRooms.getRoomById(fromPServedUser(props.room.id))
  if (!room) {
    callInProgress.value = false
    return
  }
  const awaitAutoAnswer = registerAutoAnswer(room.pServedUser, true)

  let number: string
  if (tel) {
    number = `${PSTNGUEST}-${tel}`
  } else {
    if (isExternalContact.value) {
      number = `${PSTNGUEST}-`
      if (props.member.internalNumber) {
        number += props.member.internalNumber
      } else {
        number += props.member.mobilePhone
      }
    } else {
      number = props.member.internalNumber
    }
  }

  memberCall(props.room.pServedUser, number)
    .then(() => awaitAutoAnswer)
    .catch((e: Error) => {
      console.error(e)
    })
    .finally(() => {
      callInProgress.value = false
    })
}

const handDown = async () => {
  if (handDownInProgress.value) {
    return
  }

  try {
    handDownInProgress.value = true

    await handOff(props.room.pServedUser, props.member.internalNumber)
  } catch {
    notifyWarning(t('сommon.errorWhileRequest'))
  } finally {
    handDownInProgress.value = false
  }
}

const kick = async () => {
  if (memberKickInProgress.value) {
    return
  }

  try {
    memberKickInProgress.value = true

    await memberKick(pServed.value, memberInternalNumber.value)
  } catch {
    notifyWarning(t('сommon.errorWhileRequest'))
  } finally {
    memberKickInProgress.value = false
  }
}

const goToPrivateChat = () => {
  optionalPush(getChatRoute(props.member))
}

const muteEveryoneExceptThisParticipant = async (participantCallId?: string) => {
  if (isEveryoneMuting.value) {
    return
  }

  try {
    isEveryoneMuting.value = true

    const promises: Promise<boolean>[] = []
    conferenceParticipants.value.forEach(({ internalNumber, callId }) => {
      const status = getParticipantStatus(pServed.value, internalNumber, callId ?? '')
      const mediaStatus = getParticipantMediaStatus(callId)

      if (
        callId === participantCallId ||
        status !== MemberStatus.Active ||
        mediaStatus?.isAudioMuted
      ) {
        return
      }

      promises.push(memberMuteAudio(pServed.value, internalNumber, callId))
    })

    await Promise.all(promises)
  } catch {
    notifyWarning(t('сommon.errorWhileRequest'))
  } finally {
    isEveryoneMuting.value = false
  }
}

const banAudio = async (callId: string) => {
  try {
    isMicBanInProgress.value = true

    const isAudioUnMuted = participants.value.map(p => getParticipantMediaStatus(p.callId)).find(p => !p?.isAudioMuted)
    if (isAudioUnMuted) {
      await memberMuteAudio(pServed.value, memberInternalNumber.value, callId)
    }

    await sendAudioEnablingBanned(pServed.value, callId)
  } catch (e) {
    console.error(e)
  } finally {
    isMicBanInProgress.value = false
  }
}

const banVideo = async (callId: string) => {
  try {
    isVideoBanInProgress.value = true

    const isVideoUnMuted = participants.value.map(p => getParticipantMediaStatus(p.callId)).find(p => !p?.isVideoMuted)
    if (isVideoUnMuted) {
      await memberMuteVideo(pServed.value, memberInternalNumber.value, callId)
    }
    await sendVideoEnablingBanned(pServed.value, callId)
  } catch (e) {
    console.error(e)
  } finally {
    isVideoBanInProgress.value = false
  }
}

const enableAudio = async (callId: string) => {
  try {
    isMicEnableInProgress.value = true

    await sendAudioEnablingAllowed(pServed.value, callId)
  } catch (e) {
    console.error(e)
  } finally {
    isMicEnableInProgress.value = false
  }
}

const enableVideo = async (callId: string) => {
  try {
    isVideoEnableInProgress.value = true

    await sendVideoEnablingAllowed(pServed.value, callId)
  } catch (e) {
    console.error(e)
  } finally {
    isVideoEnableInProgress.value = false
  }
}

const requestAudioEnabling = async (callId: string) => {
  try {
    isMicEnablingRequestInProgress.value = true

    await sendMediaEnablingRequest(pServed.value, callId, true)
  } catch (e) {
    console.error(e)
  } finally {
    isMicEnablingRequestInProgress.value = false
  }
}

const requestVideoEnabling = async (callId: string) => {
  try {
    isVideoEnablingRequestInProgress.value = true

    await sendMediaEnablingRequest(pServed.value, callId, false, true)
  } catch (e) {
    console.error(e)
  } finally {
    isVideoEnablingRequestInProgress.value = false
  }
}

const requestMediaEnabling = async (callId: string) => {
  try {
    isMediaEnablingRequestInProgress.value = true

    await sendMediaEnablingRequest(pServed.value, callId, true, true)
  } catch (e) {
    console.error(e)
  } finally {
    isMediaEnablingRequestInProgress.value = false
  }
}

const onClick = (item: CallSessionMemberMenuItemType) => {
  if (item.disabled) {
    return
  }

  const typedCallId = item.callId as string

  if (item.id === 'seeProfile') {
    emit('seeProfileClick')
  }

  if (item.id === 'changeRole') {
    changeRole()
  }

  if (item.id === 'toggleMic') {
    toggleMic(typedCallId)
  }

  if (item.id === 'toggleVideo') {
    toggleVideo(typedCallId)
  }

  if (item.id === 'toggleHold') {
    toggleHold(typedCallId)
  }

  if (item.id === 'call') {
    call()
  }

  if (item.id === 'handDown') {
    handDown()
  }

  if (item.id === 'kickMember') {
    kick()
  }

  if (item.id === 'privateMessage') {
    goToPrivateChat()
  }

  if (item.id === 'pin') {
    emit('togglePin')
  }

  if (item.id === 'muteEveryoneExceptThisParticipant') {
    muteEveryoneExceptThisParticipant(typedCallId)
  }

  if (item.id === 'banAudio') {
    banAudio(typedCallId)
  }

  if (item.id === 'banVideo') {
    banVideo(typedCallId)
  }

  if (item.id === 'enableAudio') {
    enableAudio(typedCallId)
  }

  if (item.id === 'enableVideo') {
    enableVideo(typedCallId)
  }

  if (item.id === 'requestAudioEnabling') {
    requestAudioEnabling(typedCallId)
  }

  if (item.id === 'requestVideoEnabling') {
    requestVideoEnabling(typedCallId)
  }

  if (item.id === 'requestMediaEnabling') {
    requestMediaEnabling(typedCallId)
  }

  emit('close')
}
</script>
